'use client';

import {
  useEffect,
  useLayoutEffect,
  useRef,
  type MutableRefObject,
} from 'react';
import { useLocale } from 'next-intl';
import { useAuth } from '@/lib/auth';
import type { LiveVoiceSession } from '@/lib/live-voice-session';
import type { OrgChatInterfaceContextValue } from '@/contexts/org-chat-interface-context';
import type { RealtimeVoiceCallControllerArgs } from './realtime-voice-call-controller-args';
import type { QueuedRealtimeInternalMessage } from '@/lib/realtime-voice-agent';
import { readDesktopDeviceApprovalSubscription } from '@/lib/desktop-device-approval-subscription';
import { createDesktopApprovalVoiceAdapter } from './desktop-device-approval-voice';

type Args = {
  call: RealtimeVoiceCallControllerArgs;
  orgThreadId?: string | null;
  orgChat?: OrgChatInterfaceContextValue;
  sessionRef: MutableRefObject<LiveVoiceSession | null>;
  leaseSessionIdRef: MutableRefObject<string | null>;
  activeCallTokenRef: MutableRefObject<number>;
  connectionVersion: number;
  leaseVersion: number;
  queue(item: QueuedRealtimeInternalMessage): boolean;
};
export function useDesktopDeviceApprovalVoice(args: Args) {
  const { user: viewer, isAuthenticated } = useAuth();
  const locale = useLocale();
  const latest = useRef({
    args,
    locale,
    userId: isAuthenticated ? viewer?.userId : null,
  });
  // Commit fresh caller context before passive subscriptions refresh. This runs
  // after every committed render so the persistent adapter never keeps old args.
  useLayoutEffect(() => {
    latest.current = {
      args,
      locale,
      userId: isAuthenticated ? viewer?.userId : null,
    };
  });
  const adapter = useRef<ReturnType<
    typeof createDesktopApprovalVoiceAdapter
  > | null>(null);
  useEffect(() => {
    const active = createDesktopApprovalVoiceAdapter({
      now: Date.now,
      getContext: () => {
        const current = latest.current;
        const { call, sessionRef, leaseSessionIdRef, activeCallTokenRef } =
          current.args;
        const threadId = current.args.orgThreadId ?? null;
        const workspaceId =
          call.organizationId ??
          current.args.orgChat?.orgThread?.organizationId ??
          null;
        const visible =
          typeof document !== 'undefined' &&
          document.visibilityState === 'visible' &&
          call.isSourceChatVisible !== false &&
          Array.from(
            document.querySelectorAll<HTMLElement>(
              '[data-thread-composer-anchor][data-desktop-org-thread-id]'
            )
          ).some((element) => {
            const rect = element.getBoundingClientRect();
            return (
              element.dataset.desktopOrgThreadId === threadId &&
              element.dataset.desktopOwnerId === current.userId &&
              element.dataset.desktopWorkspaceId === workspaceId &&
              rect.width > 0 &&
              rect.height > 0
            );
          });
        return {
          open: call.open,
          callKey:
            call.sessionId && leaseSessionIdRef.current
              ? `${call.sessionId}:${activeCallTokenRef.current}:${leaseSessionIdRef.current}`
              : null,
          session: sessionRef.current,
          userId: current.userId ?? null,
          workspaceId,
          threadId,
          visible,
          locale: current.locale,
        };
      },
      queue: (item) => latest.current.args.queue(item),
      retireFacts: (message, captured) => {
        const current = latest.current.args;
        const session = current.sessionRef.current;
        const callKey =
          current.call.sessionId && current.leaseSessionIdRef.current
            ? `${current.call.sessionId}:${current.activeCallTokenRef.current}:${current.leaseSessionIdRef.current}`
            : null;
        if (
          current.call.open &&
          callKey === captured.callKey &&
          session &&
          session === captured.session &&
          !session.isClosing
        )
          session.appendContext(message, 'thinking');
      },
    });
    adapter.current = active;
    const subscribe = readDesktopDeviceApprovalSubscription(
      'imaiDesktop' in window ? window.imaiDesktop : null
    );
    const unsubscribe = subscribe?.((notice) => active.notice(notice));
    return () => {
      unsubscribe?.();
      active.reset();
      if (adapter.current === active) adapter.current = null;
    };
  }, []);
  useEffect(() => {
    if (!args.call.open) adapter.current?.resetCall();
    else adapter.current?.refresh();
  }, [
    args.call.open,
    args.call.sessionId,
    args.call.organizationId,
    args.call.isSourceChatVisible,
    args.orgThreadId,
    args.orgChat?.orgThread?.organizationId,
    args.connectionVersion,
    args.leaseVersion,
    isAuthenticated,
    viewer?.userId,
    locale,
  ]);
}
